export type EditableComponentType = 'inverter' | 'battery' | 'other'

export interface ComponentAddOperation {
  type: 'add'
  componentType: EditableComponentType
  componentId: number // Activation ID
  quantity?: number // For batteries and others that support quantity
  slotKey?: string // For components that need to be added to specific slots
}

export interface ComponentRemoveOperation {
  type: 'remove'
  componentType: EditableComponentType
  componentUuid: string // UUID of the component to remove
}

export interface ComponentUpdateOperation {
  type: 'update'
  componentType: EditableComponentType
  componentUuid?: string // UUID of the component to update (preferred if known)
  componentCode?: string // Component code to identify component (alternative to UUID)
  componentId?: number // Component activation ID to identify component (alternative to UUID)
  quantity?: number // Update quantity for batteries/others
}

export interface SystemPropertyUpdate {
  type: 'updateProperty'
  property: EditableSystemProperty
  value: any
}

export interface IncentiveAddOperation {
  type: 'addIncentive'
  incentiveType: 'installer' | 'customer'
  title: string
  value: number
  description?: string
  use_for_loan_pay_down?: boolean // Only for customer incentives
}

export interface IncentiveRemoveOperation {
  type: 'removeIncentive'
  incentiveType: 'installer' | 'customer'
  incentiveIndex: number
}

export interface IncentiveUpdateOperation {
  type: 'updateIncentive'
  incentiveType: 'installer' | 'customer'
  incentiveIndex: number
  title?: string
  value?: number
  description?: string
  use_for_loan_pay_down?: boolean // Only for customer incentives
}

export type SystemEditOperation =
  | ComponentAddOperation
  | ComponentRemoveOperation
  | ComponentUpdateOperation
  | SystemPropertyUpdate
  | IncentiveAddOperation
  | IncentiveRemoveOperation
  | IncentiveUpdateOperation

export const EDITABLE_SYSTEM_PROPERTIES = [
  'battery_control_scheme', // 'solar_charged' | 'self_consumption' | 'peak_demand_shaving' | etc.
  'autoString', // boolean
  'pricing_scheme_id', // number | null
  'name', // string
  'export_limit', // number | null
  'is_battery_dc_connected', // boolean
  'is_battery_dc_connected_auto', // boolean
  'generation_override', // number | null
  'self_consumption_override', // number | null
  'system_efficiency_override', // number | null
  'calculator', // number
  'utility_tariff_id_override', // number | null
  'systemPanelPlacement', // 'Roof' | 'Ground' | 'Roof & Ground'
  'weather_dataset_override', // number | null
  'load_offsettable_by_battery_fraction', // number | null - fraction (0-1) of load that battery can offset
  'load_offsettable_by_battery_cap', // number | null - maximum kW of load that battery can offset
  'global_reserve_soc_percent', // number | null - usable SOC reserve (%) for load-serving discharge
  'lp_arbitrage_reserve_soc_percent', // number | null - usable SOC reserve (%) for grid-export discharge only
  'timezoneOffset', // number - timezone offset in hours (e.g., -8 for PST, +5.5 for IST)
] as const

export type EditableSystemProperty = typeof EDITABLE_SYSTEM_PROPERTIES[number]

export interface SystemEditArgs {
  operations: SystemEditOperation[]
}

/**
 * Validates that a system edit operation is valid
 */
export function validateSystemEditOperation(operation: SystemEditOperation): { valid: boolean; error?: string } {
  switch (operation.type) {
    case 'add':
      if (!['inverter', 'battery', 'other'].includes(operation.componentType)) {
        return { valid: false, error: `Invalid component type: ${operation.componentType}` }
      }
      if (!operation.componentId || typeof operation.componentId !== 'number') {
        return { valid: false, error: 'componentId must be a number' }
      }
      return { valid: true }

    case 'remove':
      if (!['inverter', 'battery', 'other'].includes(operation.componentType)) {
        return { valid: false, error: `Invalid component type: ${operation.componentType}` }
      }
      if (!operation.componentUuid || typeof operation.componentUuid !== 'string') {
        return { valid: false, error: 'componentUuid must be a string' }
      }
      return { valid: true }

    case 'update':
      if (!['inverter', 'battery', 'other'].includes(operation.componentType)) {
        return { valid: false, error: `Invalid component type: ${operation.componentType}` }
      }
      // Require at least one identifier: UUID, code, or ID
      if (
        (!operation.componentUuid || typeof operation.componentUuid !== 'string') &&
        (!operation.componentCode || typeof operation.componentCode !== 'string') &&
        (!operation.componentId || typeof operation.componentId !== 'number')
      ) {
        return {
          valid: false,
          error:
            'Must provide componentUuid (string), componentCode (string), or componentId (number) to identify the component',
        }
      }
      if (operation.quantity !== undefined && (typeof operation.quantity !== 'number' || operation.quantity < 1)) {
        return { valid: false, error: 'quantity must be a positive number' }
      }
      return { valid: true }

    case 'updateProperty':
      // Validate property name using the single source of truth
      if (!EDITABLE_SYSTEM_PROPERTIES.includes(operation.property)) {
        return { valid: false, error: `Invalid system property: ${operation.property}` }
      }
      return { valid: true }

    case 'addIncentive':
      if (!['installer', 'customer'].includes(operation.incentiveType)) {
        return { valid: false, error: `Invalid incentive type: ${operation.incentiveType}` }
      }
      if (!operation.title || typeof operation.title !== 'string') {
        return { valid: false, error: 'title must be a non-empty string' }
      }
      if (typeof operation.value !== 'number') {
        return { valid: false, error: 'value must be a number' }
      }
      return { valid: true }

    case 'removeIncentive':
      if (!['installer', 'customer'].includes(operation.incentiveType)) {
        return { valid: false, error: `Invalid incentive type: ${operation.incentiveType}` }
      }
      if (typeof operation.incentiveIndex !== 'number' || operation.incentiveIndex < 0) {
        return { valid: false, error: 'incentiveIndex must be a non-negative number' }
      }
      return { valid: true }

    case 'updateIncentive':
      if (!['installer', 'customer'].includes(operation.incentiveType)) {
        return { valid: false, error: `Invalid incentive type: ${operation.incentiveType}` }
      }
      if (typeof operation.incentiveIndex !== 'number' || operation.incentiveIndex < 0) {
        return { valid: false, error: 'incentiveIndex must be a non-negative number' }
      }
      if (operation.value !== undefined && typeof operation.value !== 'number') {
        return { valid: false, error: 'value must be a number' }
      }
      return { valid: true }

    default:
      return { valid: false, error: `Unknown operation type: ${(operation as any).type}` }
  }
}
